import React, { useEffect, useState } from 'react';

/**
 * Props interface for the MajorEventCountdown component.
 *
 * @eventTitle - The title of the event.
 * @timeStart - The start time of the event.
 */
interface Props {
  eventTitle: string;
  timeStart: Date;
}

/**
 * MajorEventCountdown component displays a live countdown timer
 * showing the time remaining until a specified major event starts.
 *
 * @param eventTitle Title of the event being counted down to.
 * @param timeStart Date/time when the event is scheduled to start.
 */
const MajorEventCountdown: React.FC<Props> = ({ eventTitle, timeStart }) => {
  const [timeLeft, setTimeLeft] = useState('');

  /**
   * Calculates and updates the remaining time every second.
   * Stops updating once the event has started or passed.
   */
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const diff = timeStart.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft('Ongoing or past!');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [timeStart]);

  return (
    <div className='major-event-countdown'>
      <strong>Next Major Event:</strong> {eventTitle} <br />
      <span>Starts in: {timeLeft}</span>
    </div>
  );
};

export default MajorEventCountdown;
